import { action, observable, makeObservable } from "mobx";
import MockupConfigAPI from "../api/MockupConfigAPI";
import { GeneratedMockupApprovalStatus } from "../../../models/MockupConfig/GeneratedMockup";
import type {
  PlacementType,
  PSDTemplate,
  SmartObject,
  SmartObjectDefault,
  ProductColor,
  BackgroundLayer,
  GeneratedMockupSource,
  GeneratedMockupActionState,
  GeneratedMockupStatus,
  GeneratedMockupComment,
} from "../../../models/MockupConfig/index";

export class MockupConfigStore {
  mockupConfigAPI: MockupConfigAPI;

  catalogueId: number | null = null;
  placementTypes: PlacementType[] = [];
  loadingPlacementTypes = false;
  psdTemplates: PSDTemplate[] = [];
  loadingPsd = false;
  selectedPsdId: number | null = null;
  smartObjects: SmartObject[] = [];
  loadingSmartObjects = false;
  defaultsMap: Record<number, SmartObjectDefault | null> = {};
  colors: ProductColor[] = [];
  loadingColors = false;
  backgroundLayers: BackgroundLayer[] = [];
  loadingBg = false;

  // Generated mockup state (kept in the store so UI components stay minimal)
  mockupOutputs: { large: string; medium: string; small: string } | null =
    null;
  mockupReferenceId: string | null = null;
  generatingMockup = false;

  // Previously generated mockups (per store/product/psd selection)
  generatedMockups: GeneratedMockupStatus[] = [];
  managerGeneratedMockups: GeneratedMockupStatus[] = [];
  loadingManagerGeneratedMockups = false;
  lastManagerGeneratedMockupsKey: string | null = null;
  loadingGeneratedMockups = false;
  pendingGeneratedMockupsKey: string | null = null;
  deletingGeneratedMockupId: number | null = null;
  lastGeneratedMockupsKey: string | null = null;
  generatedMockupComments: GeneratedMockupComment[] = [];
  generatedMockupCommentsById: Record<number, GeneratedMockupComment[]> = {};
  loadingGeneratedMockupComments = false;
  postingGeneratedMockupComment = false;
  updatingGeneratedMockupId: number | null = null;
  approvingGeneratedMockupId: number | null = null;
  rejectingGeneratedMockupId: number | null = null;
  approvingOnBehalfGeneratedMockupId: number | null = null;
  updatingShowOnStorefrontId: number | null = null;

  storePrintingFiles: Array<{
    id: number;
    variant_sku?: string | null;
    image_url: string;
    admin_id: number | null;
    admin_name?: string | null;
    active: boolean;
    created_date: string;
    modified_date: string;
  }> = [];
  loadingStorePrintingFiles = false;
  uploadingStorePrintingFiles = false;
  lastStorePrintingFilesKey: string | null = null;

  constructor() {
    this.mockupConfigAPI = new MockupConfigAPI();
    makeObservable(this, {
      catalogueId: observable,
      placementTypes: observable,
      loadingPlacementTypes: observable,
      psdTemplates: observable,
      loadingPsd: observable,
      selectedPsdId: observable,
      smartObjects: observable,
      loadingSmartObjects: observable,
      defaultsMap: observable,
      colors: observable,
      loadingColors: observable,
      backgroundLayers: observable,
      loadingBg: observable,

      // Generated mockup state
      mockupOutputs: observable,
      mockupReferenceId: observable,
      generatingMockup: observable,
      generatedMockups: observable,
      managerGeneratedMockups: observable,
      loadingManagerGeneratedMockups: observable,
      lastManagerGeneratedMockupsKey: observable,
      loadingGeneratedMockups: observable,
      deletingGeneratedMockupId: observable,
      lastGeneratedMockupsKey: observable,
      generatedMockupComments: observable,
      generatedMockupCommentsById: observable,
      loadingGeneratedMockupComments: observable,
      postingGeneratedMockupComment: observable,
      updatingGeneratedMockupId: observable,
      approvingGeneratedMockupId: observable,
      rejectingGeneratedMockupId: observable,
      approvingOnBehalfGeneratedMockupId: observable,
      updatingShowOnStorefrontId: observable,
      storePrintingFiles: observable,
      loadingStorePrintingFiles: observable,
      uploadingStorePrintingFiles: observable,
      lastStorePrintingFilesKey: observable,

      setCatalogueId: action,
      setSelectedPsdId: action,
      setPsdTemplateBlankPreviewUrl: action,
      resetProductData: action,
      resetGeneratedMockupData: action,
      setGeneratedMockupOutputs: action,
      setMockupReferenceId: action,
      setGeneratingMockup: action,
      uploadManualMockup: action,
      setDeletingGeneratedMockupId: action,
      clearManagerGeneratedMockups: action,
      fetchGeneratedMockups: action,
      fetchManagerGeneratedMockupsByProduct: action,
      deleteGeneratedMockup: action,
      fetchGeneratedMockupComments: action,
      addGeneratedMockupComment: action,
      updateGeneratedMockup: action,
      approveGeneratedMockup: action,
      setGeneratedMockupShowOnStorefront: action,
      rejectGeneratedMockup: action,
      approveGeneratedMockupOnBehalfUser: action,
      fetchStorePrintingFiles: action,
      saveStorePrintingFiles: action,
      deleteStorePrintingFile: action,
      clearStorePrintingFiles: action,
      getPlacementTypes: action,
      getPSDTemplates: action,
      getSmartObjects: action,
      getColors: action,
      getBackgroundLayers: action,
      createPSDTemplate: action,
      updatePSDTemplate: action,
      deletePSDTemplate: action,
      createSmartObject: action,
      updateSmartObject: action,
      deleteSmartObject: action,
      createSmartObjectDefault: action,
      updateSmartObjectDefault: action,
      createColor: action,
      deleteColor: action,
      createBackgroundLayer: action,
      deleteBackgroundLayer: action,
    });
  }

  setCatalogueId = (id: number | null) => {
    this.catalogueId = id;
  };

  setSelectedPsdId = (id: number | null) => {
    this.selectedPsdId = id;
  };

  setPsdTemplateBlankPreviewUrl = (id: number, blankPreviewUrl: string) => {
    this.psdTemplates = this.psdTemplates.map((template) =>
      template.id === id
        ? { ...template, blank_preview_url: blankPreviewUrl }
        : template,
    );
  };

  resetProductData = () => {
    this.psdTemplates = [];
    this.selectedPsdId = null;
    this.smartObjects = [];
    this.colors = [];
    this.backgroundLayers = [];
    this.defaultsMap = {};
    this.clearStorePrintingFiles();
  };

  resetGeneratedMockupData = () => {
    this.mockupOutputs = null;
    this.mockupReferenceId = null;
  };

  setGeneratedMockupOutputs = (outputs: {
    large: string;
    medium: string;
    small: string;
  } | null) => {
    this.mockupOutputs = outputs;
  };

  setMockupReferenceId = (referenceId: string | null) => {
    this.mockupReferenceId = referenceId;
  };

  setGeneratingMockup = (value: boolean) => {
    this.generatingMockup = value;
  };

  uploadManualMockup = async (payload: {
    customer_id: number;
    store_id: number | string;
    product_id: number;
    mockup_urls: string[];
    psd_template_id?: number | null;
    variant_sku?: string | null;
    source?: "manual" | "generated";
  }): Promise<{
    items: Array<{ id: number; mockup_url: string }>;
  }> => {
    this.generatingMockup = true;
    try {
      return await this.mockupConfigAPI.uploadManualMockup(payload);
    } finally {
      this.generatingMockup = false;
    }
  };

  clearStorePrintingFiles = () => {
    this.storePrintingFiles = [];
    this.lastStorePrintingFilesKey = null;
  };

  fetchStorePrintingFiles = async (
    payload: { store_id: string | number; product_id: number; variant_sku?: string | null },
    options?: { force?: boolean },
  ) => {
    const key = `${payload.store_id}:${payload.product_id}:${payload.variant_sku ?? ""}`;
    if (!options?.force && this.lastStorePrintingFilesKey === key) {
      return this.storePrintingFiles;
    }

    this.loadingStorePrintingFiles = true;
    try {
      const files = await this.mockupConfigAPI.getStorePrintingFiles(payload);
      this.storePrintingFiles = files;
      this.lastStorePrintingFilesKey = key;
      return files;
    } finally {
      this.loadingStorePrintingFiles = false;
    }
  };

  saveStorePrintingFiles = async (payload: {
    store_id: string | number;
    product_id: number;
    image_urls: string[];
    variant_sku: string;
  }) => {
    this.uploadingStorePrintingFiles = true;
    try {
      const files = await this.mockupConfigAPI.saveStorePrintingFiles(payload);
      this.storePrintingFiles = files;
      this.lastStorePrintingFilesKey = `${payload.store_id}:${payload.product_id}:${payload.variant_sku ?? ""}`;
      return files;
    } finally {
      this.uploadingStorePrintingFiles = false;
    }
  };

  deleteStorePrintingFile = async (payload: {
    id: number;
    store_id: string | number;
    product_id: number;
  }) => {
    await this.mockupConfigAPI.deleteStorePrintingFile(payload);
    this.storePrintingFiles = this.storePrintingFiles.filter(
      (file) => file.id !== payload.id,
    );
    this.lastStorePrintingFilesKey = `${payload.store_id}:${payload.product_id}`;
  };

  setDeletingGeneratedMockupId = (id: number | null) => {
    this.deletingGeneratedMockupId = id;
  };

  clearManagerGeneratedMockups = () => {
    this.managerGeneratedMockups = [];
    this.lastManagerGeneratedMockupsKey = null;
  };

  private deriveMockupOutputsFromStoredUrl(url: string): {
    large: string;
    medium: string;
    small: string;
  } {
    const match = url.match(/\/(large|medium|small)\.webp$/i);
    if (!match) {
      return { large: url, medium: url, small: url };
    }
    const base = url.replace(/\/(large|medium|small)\.webp$/i, "");
    return {
      large: `${base}/large.webp`,
      medium: `${base}/medium.webp`,
      small: `${base}/small.webp`,
    };
  }

  fetchGeneratedMockups = async (payload: {
    store_id: string;
    product_id: number;
    psd_template_id: number;
  }, options?: { force?: boolean }) => {
    const key = `${payload.store_id}:${payload.product_id}:${payload.psd_template_id}`;
    if (!options?.force && this.lastGeneratedMockupsKey === key) {
      const first = this.generatedMockups?.[0] ?? null;
      if (!first?.mockup_url) {
        this.resetGeneratedMockupData();
        return;
      }
      this.setMockupReferenceId(first.reference_id ?? null);
      this.setGeneratedMockupOutputs(this.deriveMockupOutputsFromStoredUrl(first.mockup_url));
      return;
    }
    if (this.pendingGeneratedMockupsKey === key) {
      return;
    }

    this.pendingGeneratedMockupsKey = key;
    this.loadingGeneratedMockups = true;
    try {
      const records = await this.mockupConfigAPI.getGeneratedMockups(payload);
      const safeRecords = records ?? [];
      this.generatedMockups = safeRecords.map(this.mapGeneratedMockupToListItem);
      this.lastGeneratedMockupsKey = key;

      const first = safeRecords?.[0] ?? null;
      if (!first?.mockup_url) {
        this.resetGeneratedMockupData();
        return;
      }

      this.setMockupReferenceId(first.reference_id ?? null);
      this.setGeneratedMockupOutputs(
        this.deriveMockupOutputsFromStoredUrl(first.mockup_url),
      )
    } catch (e) {
      throw e;
    } finally {
      if (this.pendingGeneratedMockupsKey === key) {
        this.pendingGeneratedMockupsKey = null;
      }
      this.loadingGeneratedMockups = false;
    }
  };

  fetchManagerGeneratedMockupsByProduct = async (payload: {
    store_id: string;
    product_id: number;
    include_inactive?: boolean;
  }, options?: { force?: boolean }) => {
    const key = `${payload.store_id}:${payload.product_id}:${payload.include_inactive ? 1 : 0}`;
    if (!options?.force && this.lastManagerGeneratedMockupsKey === key) {
      return;
    }
    this.loadingManagerGeneratedMockups = true;
    try {
      const records = await this.mockupConfigAPI.getGeneratedMockupsByProduct(payload);
      const merged = (records ?? []).map(this.mapGeneratedMockupToListItem);
      const uniqueById = Array.from(
        new Map(merged.map((m) => [m.id, m])).values(),
      ).sort(
        (a, b) =>
          new Date(b.updated_at || b.created_at).getTime() -
          new Date(a.updated_at || a.created_at).getTime(),
      );
      this.managerGeneratedMockups = uniqueById;
      this.lastManagerGeneratedMockupsKey = key;
    } catch (e) {
      throw e;
    } finally {
      this.loadingManagerGeneratedMockups = false;
    }
  };

  private mapGeneratedMockupToListItem = (r: any): GeneratedMockupStatus => ({
    id: r.id,
    variant_sku: r.variant_sku ?? null,
    source: r.source as GeneratedMockupSource,
    psd_template_id: r.psd_template_id ?? undefined,
    reference_id: r.reference_id ?? null,
    mockup_url: r.mockup_url,
    approval_status: r.approval_status ?? GeneratedMockupApprovalStatus.PENDING,
    rejection_reason: r.rejection_reason ?? null,
    rejection_evidence_url: r.rejection_evidence_url ?? null,
    created_at: r.created_at,
    updated_at: r.updated_at,
    approved_by_admin_id: r.approved_by_admin_id ?? null,
    approved_by_admin_name: r.approved_by_admin_name ?? null,
    approved_by_user_id: r.approved_by_user_id ?? null,
    approved_by_user_name: r.approved_by_user_name ?? null,
    approved_on_behalf_by_admin_id: r.approved_on_behalf_by_admin_id ?? null,
    approved_on_behalf_by_admin_name: r.approved_on_behalf_by_admin_name ?? null,
    approved_on_behalf_reason: r.approved_on_behalf_reason ?? null,
    approved_on_behalf_evidence_url: r.approved_on_behalf_evidence_url ?? null,
    rejected_by_admin_id: r.rejected_by_admin_id ?? null,
    rejected_by_admin_name: r.rejected_by_admin_name ?? null,
    rejected_by_user_id: r.rejected_by_user_id ?? null,
    rejected_by_user_name: r.rejected_by_user_name ?? null,
    rejected_at: r.rejected_at ?? null,
    iteration_no: r.iteration_no ?? null,
    is_rejected: r.is_rejected,
    is_fully_approved: r.is_fully_approved,
    can_delete: r.can_delete,
    show_on_storefront:
      r.show_on_storefront === 0 || r.show_on_storefront === false ? 0 : 1,
  });

  private patchListItemState = (
    list: GeneratedMockupStatus[],
    state: GeneratedMockupActionState,
  ): GeneratedMockupStatus[] =>
    list.map((m) =>
      m.id !== state.id
        ? m
        : {
          ...m,
          source: state.source ?? m.source,
          mockup_url: state.mockup_url ?? m.mockup_url,
          approval_status:
            state.approval_status === GeneratedMockupApprovalStatus.APPROVED
              ? GeneratedMockupApprovalStatus.APPROVED
              : state.approval_status === GeneratedMockupApprovalStatus.REJECTED
                ? GeneratedMockupApprovalStatus.REJECTED
                : state.approval_status ===
                  GeneratedMockupApprovalStatus.CHANGES_REQUESTED
                  ? GeneratedMockupApprovalStatus.CHANGES_REQUESTED
                  : state.approval_status === GeneratedMockupApprovalStatus.PENDING
                    ? GeneratedMockupApprovalStatus.PENDING
                    : m.approval_status ?? GeneratedMockupApprovalStatus.PENDING,
          rejection_reason:
            state.rejection_reason !== undefined
              ? state.rejection_reason
              : m.rejection_reason ?? null,
          approved_by_admin_id:
            state.approved_by_admin_id !== undefined
              ? state.approved_by_admin_id
              : m.approved_by_admin_id ?? null,
          approved_by_admin_name:
            state.approved_by_admin_name !== undefined
              ? state.approved_by_admin_name
              : m.approved_by_admin_name ?? null,
          approved_by_user_id:
            state.approved_by_user_id !== undefined
              ? state.approved_by_user_id
              : m.approved_by_user_id ?? null,
          approved_by_user_name:
            state.approved_by_user_name !== undefined
              ? state.approved_by_user_name
              : m.approved_by_user_name ?? null,
          approved_on_behalf_by_admin_id:
            state.approved_on_behalf_by_admin_id !== undefined
              ? state.approved_on_behalf_by_admin_id
              : m.approved_on_behalf_by_admin_id ?? null,
          approved_on_behalf_by_admin_name:
            state.approved_on_behalf_by_admin_name !== undefined
              ? state.approved_on_behalf_by_admin_name
              : m.approved_on_behalf_by_admin_name ?? null,
          approved_on_behalf_reason:
            state.approved_on_behalf_reason !== undefined
              ? state.approved_on_behalf_reason
              : m.approved_on_behalf_reason ?? null,
          approved_on_behalf_evidence_url:
            state.approved_on_behalf_evidence_url !== undefined
              ? state.approved_on_behalf_evidence_url
              : m.approved_on_behalf_evidence_url ?? null,
          iteration_no: state.iteration_no ?? m.iteration_no ?? null,
          is_rejected:
            state.is_rejected !== undefined ? state.is_rejected : m.is_rejected,
          is_fully_approved:
            state.is_fully_approved !== undefined
              ? state.is_fully_approved
              : m.is_fully_approved,
          can_delete: state.can_delete !== undefined ? state.can_delete : m.can_delete,
          updated_at: state.updated_at ?? new Date().toISOString(),
        },
    );

  private applyGeneratedMockupState = (state: GeneratedMockupActionState) => {
    this.generatedMockups = this.patchListItemState(this.generatedMockups, state);
    this.managerGeneratedMockups = this.patchListItemState(
      this.managerGeneratedMockups,
      state,
    );
  };

  deleteGeneratedMockup = async (id: number) => {
    this.setDeletingGeneratedMockupId(id);
    try {
      await this.mockupConfigAPI.deleteGeneratedMockup(id);
      this.generatedMockups = this.generatedMockups.filter((m) => m.id !== id);
      this.managerGeneratedMockups = this.managerGeneratedMockups.filter(
        (m) => m.id !== id,
      );
      const first = this.generatedMockups?.[0] ?? null;
      if (!first?.mockup_url) {
        this.resetGeneratedMockupData();
        return;
      }
      this.setMockupReferenceId(first.reference_id ?? null);
      this.setGeneratedMockupOutputs(
        this.deriveMockupOutputsFromStoredUrl(first.mockup_url),
      );
    } catch (e) {
      throw e;
    } finally {
      this.setDeletingGeneratedMockupId(null);
    }
  };

  fetchGeneratedMockupComments = async (id: number) => {
    const cached = this.generatedMockupCommentsById[id];
    if (cached) {
      this.generatedMockupComments = cached;
      return;
    }
    this.loadingGeneratedMockupComments = true;
    try {
      const comments = await this.mockupConfigAPI.getGeneratedMockupComments(id);
      this.generatedMockupComments = comments;
      this.generatedMockupCommentsById[id] = comments;
    } catch (e) {
      throw e;
    } finally {
      this.loadingGeneratedMockupComments = false;
    }
  };

  addGeneratedMockupComment = async (payload: {
    generated_mockup_id: number;
    comment: string;
  }) => {
    this.postingGeneratedMockupComment = true;
    try {
      const created = await this.mockupConfigAPI.addGeneratedMockupComment(payload);
      if (!created || !created.id) {
        throw new Error("Failed to add mockup comment");
      }

      const comment: GeneratedMockupComment = {
        id: Date.now(),
        generated_mockup_id: payload.generated_mockup_id,
        comment: payload.comment,
        admin_id: null,
        user_id: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      comment.id = created.id ?? comment.id;
      comment.generated_mockup_id = created.generated_mockup_id ?? comment.generated_mockup_id;
      comment.comment = created.comment ?? comment.comment;
      comment.admin_id = (created as any).admin_id ?? comment.admin_id;
      comment.user_id = (created as any).user_id ?? comment.user_id;
      comment.created_at = (created as any).created_at ?? comment.created_at;
      comment.updated_at = (created as any).updated_at ?? comment.updated_at;

      const existing = this.generatedMockupCommentsById[payload.generated_mockup_id] ?? [];
      const next = [...existing, comment];
      this.generatedMockupCommentsById[payload.generated_mockup_id] = next;
      this.generatedMockupComments = next;
    } catch (e) {
      throw e;
    } finally {
      this.postingGeneratedMockupComment = false;
    }
  };

  updateGeneratedMockup = async (payload: {
    id: number;
    mockup_url: string;
    source: GeneratedMockupSource;
  }): Promise<GeneratedMockupActionState> => {
    this.updatingGeneratedMockupId = payload.id;
    try {
      const state = await this.mockupConfigAPI.updateGeneratedMockup(payload);
      this.applyGeneratedMockupState(state);
      return state;
    } catch (e) {
      throw e;
    } finally {
      this.updatingGeneratedMockupId = null;
    }
  };

  setGeneratedMockupShowOnStorefront = async (id: number, showOnStorefront: boolean) => {
    this.updatingShowOnStorefrontId = id;
    try {
      const result = await this.mockupConfigAPI.setGeneratedMockupShowOnStorefront(
        id,
        showOnStorefront,
      );
      const flag = result?.show_on_storefront === 0 ? 0 : 1;
      const patch = (list: GeneratedMockupStatus[]) =>
        list.map((mockup) =>
          mockup.id === id ? { ...mockup, show_on_storefront: flag } : mockup,
        );
      this.managerGeneratedMockups = patch(this.managerGeneratedMockups);
      this.generatedMockups = patch(this.generatedMockups);
      return result;
    } catch (e) {
      throw e;
    } finally {
      this.updatingShowOnStorefrontId = null;
    }
  };

  approveGeneratedMockup = async (
    id: number,
  ): Promise<GeneratedMockupActionState> => {
    this.approvingGeneratedMockupId = id;
    try {
      const state = await this.mockupConfigAPI.approveGeneratedMockup(id);
      this.applyGeneratedMockupState(state);
      return state;
    } catch (e) {
      throw e;
    } finally {
      this.approvingGeneratedMockupId = null;
    }
  };

  approveGeneratedMockupOnBehalfUser = async (payload: {
    id: number;
    reason: string;
    evidence_url: string;
  }): Promise<GeneratedMockupActionState> => {
    this.approvingOnBehalfGeneratedMockupId = payload.id;
    try {
      const state = await this.mockupConfigAPI.approveGeneratedMockupOnBehalfUser(
        payload,
      );
      this.applyGeneratedMockupState(state);
      return state;
    } catch (e) {
      throw e;
    } finally {
      this.approvingOnBehalfGeneratedMockupId = null;
    }
  };

  rejectGeneratedMockup = async (payload: {
    id: number;
    rejection_reason: string;
    evidence_url?: string;
  }): Promise<GeneratedMockupActionState> => {
    this.rejectingGeneratedMockupId = payload.id;
    try {
      const state = await this.mockupConfigAPI.rejectGeneratedMockup(payload);
      this.generatedMockups = this.generatedMockups.filter((m) => m.id !== payload.id);
      this.managerGeneratedMockups = this.managerGeneratedMockups.filter(
        (m) => m.id !== payload.id,
      );
      delete this.generatedMockupCommentsById[payload.id];
      if (this.generatedMockupComments?.length) {
        this.generatedMockupComments = this.generatedMockupComments.filter(
          (comment) => comment.generated_mockup_id !== payload.id,
        );
      }
      return state;
    } catch (e) {
      throw e;
    } finally {
      this.rejectingGeneratedMockupId = null;
    }
  };

  getPlacementTypes = async () => {
    this.loadingPlacementTypes = true;
    try {
      this.placementTypes = await this.mockupConfigAPI.getPlacementTypes();
    } catch (e) {
      throw e;
    } finally {
      this.loadingPlacementTypes = false;
    }
  };

  getPSDTemplates = async (catalogueId: number) => {
    this.loadingPsd = true;
    try {
      this.psdTemplates =
        await this.mockupConfigAPI.getPSDTemplates(catalogueId);
      if (
        this.psdTemplates.length > 0 &&
        !this.psdTemplates.some((p) => p.id === this.selectedPsdId)
      ) {
        this.selectedPsdId = this.psdTemplates[0].id ?? null;
      }
    } catch (e) {
      throw e;
    } finally {
      this.loadingPsd = false;
    }
  };

  getSmartObjects = async (psdTemplateId: number) => {
    this.loadingSmartObjects = true;
    try {
      this.smartObjects =
        await this.mockupConfigAPI.getSmartObjects(psdTemplateId);
      const defaults: Record<number, SmartObjectDefault | null> = {};
      for (const so of this.smartObjects) {
        if (so.id) {
          const d = await this.mockupConfigAPI.getSmartObjectDefaults(so.id);
          defaults[so.id] = d;
        }
      }
      this.defaultsMap = defaults;
    } catch (e) {
      throw e;
    } finally {
      this.loadingSmartObjects = false;
    }
  };

  getColors = async (catalogueId: number) => {
    this.loadingColors = true;
    try {
      this.colors = await this.mockupConfigAPI.getColors(catalogueId);
    } catch (e) {
      throw e;
    } finally {
      this.loadingColors = false;
    }
  };

  getBackgroundLayers = async (psdTemplateId: number) => {
    this.loadingBg = true;
    try {
      this.backgroundLayers =
        await this.mockupConfigAPI.getBackgroundLayers(psdTemplateId);
    } catch (e) {
      throw e;
    } finally {
      this.loadingBg = false;
    }
  };

  createPSDTemplate = async (
    data: Omit<PSDTemplate, "id">,
  ): Promise<PSDTemplate & { id: number }> => {
    try {
      const created = await this.mockupConfigAPI.createPSDTemplate(data);
      if (this.catalogueId) {
        await this.getPSDTemplates(this.catalogueId);
      }
      return created;
    } catch (e) {
      throw e;
    }
  };

  updatePSDTemplate = async (
    id: number,
    data: Partial<PSDTemplate>,
  ): Promise<PSDTemplate> => {
    try {
      const updated = await this.mockupConfigAPI.updatePSDTemplate(id, data);
      if (this.catalogueId) {
        await this.getPSDTemplates(this.catalogueId);
      }
      return updated;
    } catch (e) {
      throw e;
    }
  };

  deletePSDTemplate = async (id: number): Promise<void> => {
    try {
      await this.mockupConfigAPI.deletePSDTemplate(id);
      if (this.selectedPsdId === id) {
        this.selectedPsdId = null;
      }
      if (this.catalogueId) {
        await this.getPSDTemplates(this.catalogueId);
      }
    } catch (e) {
      throw e;
    }
  };

  createSmartObject = async (
    data: Omit<SmartObject, "id">,
  ): Promise<SmartObject & { id: number }> => {
    try {
      const created = await this.mockupConfigAPI.createSmartObject(data);
      if (this.selectedPsdId) {
        await this.getSmartObjects(this.selectedPsdId);
      }
      return created;
    } catch (e) {
      throw e;
    }
  };

  updateSmartObject = async (
    id: number,
    data: Partial<SmartObject>,
  ): Promise<SmartObject> => {
    try {
      const updated = await this.mockupConfigAPI.updateSmartObject(id, data);
      if (this.selectedPsdId) {
        await this.getSmartObjects(this.selectedPsdId);
      }
      return updated;
    } catch (e) {
      throw e;
    }
  };

  deleteSmartObject = async (id: number): Promise<void> => {
    try {
      await this.mockupConfigAPI.deleteSmartObject(id);
      if (this.selectedPsdId) {
        await this.getSmartObjects(this.selectedPsdId);
      }
    } catch (e) {
      throw e;
    }
  };

  createSmartObjectDefault = async (
    data: Omit<SmartObjectDefault, "id">,
  ): Promise<SmartObjectDefault & { id: number }> => {
    try {
      const created = await this.mockupConfigAPI.createSmartObjectDefault(data);
      if (this.selectedPsdId) {
        await this.getSmartObjects(this.selectedPsdId);
      }
      return created;
    } catch (e) {
      throw e;
    }
  };

  updateSmartObjectDefault = async (
    smartObjectId: number,
    data: Partial<SmartObjectDefault>,
  ): Promise<SmartObjectDefault> => {
    try {
      const updated = await this.mockupConfigAPI.updateSmartObjectDefault(
        smartObjectId,
        data,
      );
      if (this.selectedPsdId) {
        await this.getSmartObjects(this.selectedPsdId);
      }
      return updated;
    } catch (e) {
      throw e;
    }
  };

  createColor = async (
    data: Omit<ProductColor, "id">,
  ): Promise<ProductColor & { id: number }> => {
    try {
      const created = await this.mockupConfigAPI.createColor(data);
      if (this.catalogueId) {
        await this.getColors(this.catalogueId);
      }
      return created;
    } catch (e) {
      throw e;
    }
  };

  deleteColor = async (id: number): Promise<void> => {
    try {
      await this.mockupConfigAPI.deleteColor(id);
      if (this.catalogueId) {
        await this.getColors(this.catalogueId);
      }
    } catch (e) {
      throw e;
    }
  };

  createBackgroundLayer = async (
    data: Omit<BackgroundLayer, "id">,
  ): Promise<BackgroundLayer & { id: number }> => {
    try {
      const created = await this.mockupConfigAPI.createBackgroundLayer(data);
      if (this.selectedPsdId) {
        await this.getBackgroundLayers(this.selectedPsdId);
      }
      return created;
    } catch (e) {
      throw e;
    }
  };

  deleteBackgroundLayer = async (id: number): Promise<void> => {
    try {
      await this.mockupConfigAPI.deleteBackgroundLayer(id);
      if (this.selectedPsdId) {
        await this.getBackgroundLayers(this.selectedPsdId);
      }
    } catch (e) {
      throw e;
    }
  };

  saveSmartObjectDefaults = async (
    smartObjectId: number,
    form: Partial<SmartObjectDefault>,
  ): Promise<void> => {
    try {
      const payload = {
        smart_object_id: smartObjectId,
        default_x: form.default_x ?? 0,
        default_y: form.default_y ?? 0,
        default_width: form.default_width ?? 0,
        default_height: form.default_height ?? 0,
        min_width: form.min_width ?? null,
        max_width: form.max_width ?? null,
        aspect_ratio_lock: form.aspect_ratio_lock ?? true,
      };
      const existing =
        await this.mockupConfigAPI.getSmartObjectDefaults(smartObjectId);
      if (existing && existing.id) {
        await this.mockupConfigAPI.updateSmartObjectDefault(
          smartObjectId,
          payload,
        );
      } else {
        await this.mockupConfigAPI.createSmartObjectDefault(payload);
      }
      if (this.selectedPsdId) {
        await this.getSmartObjects(this.selectedPsdId);
      }
    } catch (e) {
      throw e;
    }
  };
}
